/**
 * Static per-type configuration for subchapters. Each SubChapterType drives:
 *  - which layout / slideTemplate values the LLM can pick (`allowedSlideTemplates`)
 *  - whether image → video auto-generation kicks in on speacher_front (`autoVideo`)
 *  - an additional prompt suffix appended to the per-subchapter hiddenPrompt
 *    (`buildPromptSuffix`) — receives the current visual template's hiddenPrompt
 *    for EXPLAINER (so the stickman/whiteboard/etc. brief is injected).
 *
 * The resolved hiddenPrompt is persisted on AISubChapter so "Continue via AI"
 * reproduces the same generation instructions even if this config file evolves.
 */

import {SubChapterType} from "@/models/ai/aiSubChapter.model";
import {SLIDE_TEMPLATE_VALUES} from "@/models/ai/aiVisualTemplate.model";

export interface SubChapterTypeConfig {
  /**
   * Whitelist of layout + slideTemplate values allowed for this type.
   * Empty array = defer to the dynamic getter (currently only SLIDE).
   */
  allowedSlideTemplates: string[];
  /**
   * When true, speacher_front auto-chains video generation after image
   * generation for every slide of this subchapter.
   */
  autoVideo: boolean;
  /**
   * Extra prompt appended to the per-subchapter hiddenPrompt. Receives the
   * visual template's hiddenPrompt (for EXPLAINER) — null for other types.
   * Return "" to add nothing.
   */
  buildPromptSuffix: (visualTemplateHiddenPrompt: string | null) => string;
}

export const SUB_CHAPTER_TYPE_CONFIG: Record<SubChapterType, SubChapterTypeConfig> = {
  [SubChapterType.SLIDE]: {
    // Empty = fall back to the existing dynamic getAllowedSlideTemplates()
    // driven by (visualTemplate, avatarOn, generationMode).
    allowedSlideTemplates: [],
    autoVideo: false,
    buildPromptSuffix: () => "",
  },

  [SubChapterType.EXPLAINER]: {
    // EXPLAINER is a pure visual explainer — only ILLUSTRATED_ONLY is allowed.
    // No text overlay, the illustration IS the message.
    allowedSlideTemplates: [SLIDE_TEMPLATE_VALUES.ILLUSTRATED_ONLY],
    autoVideo: true,
    buildPromptSuffix: (visualTemplateHiddenPrompt) => {
      const templateBrief = visualTemplateHiddenPrompt
        ? "\n\nVisual style to apply on every slide:\n" + visualTemplateHiddenPrompt
        : "";
      return (
        "\n\n### Subchapter type: EXPLAINER\n" +
        "This subchapter is a visually-driven video explainer. EVERY slide MUST use " +
        "layout='SLIDE' with slideTemplate='ILLUSTRATED_ONLY'. Do not include text overlays, " +
        "titles, or callouts on the slide — the illustration alone tells the story. " +
        "The narration script must fully carry the meaning; each illustration illustrates the current sentence." +
        templateBrief
      );
    },
  },

  [SubChapterType.QUIZ]: {
    // Quiz rendering flow will land in a follow-up. Keeping neutral defaults
    // so the current SLIDE-based fallback stays functional.
    allowedSlideTemplates: [],
    autoVideo: false,
    buildPromptSuffix: () => "",
  },
};

export function getSubChapterTypeConfig(type: SubChapterType | string | null | undefined): SubChapterTypeConfig {
  const key = (type as SubChapterType) || SubChapterType.SLIDE;
  return SUB_CHAPTER_TYPE_CONFIG[key] || SUB_CHAPTER_TYPE_CONFIG[SubChapterType.SLIDE];
}
